import React, { useMemo } from 'react';

// The 12 AI Capability Classes
const CAPABILITY_CLASSES = [
  { id: 1, name: 'Machine Learning', short: 'ML', description: 'Pattern recognition & classification' },
  { id: 2, name: 'Computer Vision', short: 'CV', description: 'Image & video analysis' },
  { id: 3, name: 'Generative AI', short: 'GenAI', description: 'Text generation & drafts' },
  { id: 4, name: 'Predictive Analytics', short: 'PA', description: 'Forecasting & trend analysis' },
  { id: 5, name: 'Natural Language Processing (NLP)', short: 'NLP', description: 'Text analysis & sentiment' },
  { id: 6, name: 'Speech & Voice', short: 'SV', description: 'Speech-to-text & voice control' },
  { id: 7, name: 'Optimization & Planning', short: 'OP', description: 'Resource planning & scheduling' },
  { id: 8, name: 'Recommendation', short: 'RP', description: 'Recommendations & personalization' },
  { id: 9, name: 'Conversational AI', short: 'CAI', description: 'Chatbots & assistants' },
  { id: 10, name: 'Robotics', short: 'ROB', description: 'Physical automation' },
  { id: 11, name: 'Robotic Process Automation', short: 'RPA', description: 'Rule-based digital automation' },
  { id: 12, name: 'Document Intelligence', short: 'DI', description: 'Form & document extraction' },
];

const POTENTIAL_LABELS = {
  hoch: 'Hoch',
  mittel: 'Mittel',
  niedrig: 'Niedrig',
};

export default function MappingResults({ processData, mappingData, onViewInCanvas }) {
  // Determine matched class IDs and build detail rows
  const { matchedIds, matchDetails, matchCount } = useMemo(() => {
    const matched = new Set();
    const details = [];

    if (mappingData?.mappings) {
      mappingData.mappings.forEach(m => {
        if (m.primaryCapability?.classId) {
          matched.add(m.primaryCapability.classId);
          details.push({
            classId: m.primaryCapability.classId,
            className: m.primaryCapability.class,
            stepId: m.stepId,
            stepName: processData?.steps?.find(s => s.id === m.stepId)?.name || m.stepId,
            toolDisplayName: m.toolDisplayName || '',
            toolDescription: m.toolDescription || '',
            automationPotential: m.automationPotential || 'mittel',
          });
        }
      });
    }

    return { matchedIds: matched, matchDetails: details, matchCount: matched.size };
  }, [mappingData, processData]);

  // Top 3 Lite Agent candidates from the mapping data
  const topLiteAgents = useMemo(() => {
    if (mappingData?.topLiteAgents && mappingData.topLiteAgents.length > 0) {
      return mappingData.topLiteAgents.slice(0, 3);
    }
    // Fallback: derive from mappings if topLiteAgents not present
    if (mappingData?.mappings) {
      return mappingData.mappings
        .filter(m => m.liteAgentFit?.suitable)
        .slice(0, 3)
        .map((m, i) => ({
          rank: i + 1,
          stepId: m.stepId,
          stepName: processData?.steps?.find(s => s.id === m.stepId)?.name || m.stepId,
          reason: m.liteAgentFit?.reason || m.toolDescription || '',
          automationPotential: m.automationPotential || 'hoch',
        }));
    }
    return [];
  }, [mappingData, processData]);

  // SharePoint-bridge recommendations (Topic 15): every step whose data lives
  // outside M365 gets a bridge path in liteAgentDataBridge — the prompt appends
  // the data-at-source add-on note to it, so surface it here too.
  const bridgeSteps = useMemo(() => {
    if (!mappingData?.mappings) return [];
    return mappingData.mappings
      .filter(m => m.liteAgentDataBridge && String(m.liteAgentDataBridge).trim())
      .map(m => ({
        stepId: m.stepId,
        stepName: processData?.steps?.find(s => s.id === m.stepId)?.name || m.stepId,
        bridge: String(m.liteAgentDataBridge).trim(),
      }));
  }, [mappingData, processData]);

  const totalSteps = processData?.steps?.length || 0;
  const liteAgentCount = mappingData?.summary?.liteAgentCandidates || topLiteAgents.length;

  return (
    <div className="mapping-container">
      <div className="mapping-window">
        {/* Header */}
        <div className="mapping-header">
          <h2>KI-Fähigkeiten Analyse</h2>
          <p>{matchCount} von 12 KI-Klassen passen zu deinem Prozess</p>
        </div>

        {/* Capability Grid — matched vs grayed out */}
        <div className="capability-grid">
          {CAPABILITY_CLASSES.map((cap) => {
            const isMatched = matchedIds.has(cap.id);

            return (
              <div
                key={cap.id}
                className={`capability-tile ${isMatched ? 'matched' : 'unmatched'}`}
              >
                <div className="tile-short">{cap.short}</div>
                <div className="tile-name">{cap.name}</div>
                <div className="tile-desc">{cap.description}</div>
                {isMatched && <div className="tile-match-badge">Treffer</div>}
              </div>
            );
          })}
        </div>

        {/* Stats — simplified */}
        <div className="results-summary">
          <div className="results-stat">
            <span className="results-number">{matchCount}</span>
            <span className="results-label">KI-Klassen mit Treffer</span>
          </div>
          <div className="results-stat">
            <span className="results-number">{totalSteps}</span>
            <span className="results-label">Prozessschritte</span>
          </div>
          <div className="results-stat highlight">
            <span className="results-number">{liteAgentCount}</span>
            <span className="results-label">Lite Agent Kandidaten</span>
          </div>
        </div>

        {/* ═══ TOP 3 LITE AGENT CANDIDATES ═══ */}
        {topLiteAgents.length > 0 && (
          <div className="top-lite-agents">
            <h2 className="top-lite-agents-title">Top Lite Agent Empfehlungen</h2>
            <div className="top-agent-cards">
              {topLiteAgents.map((agent, i) => (
                <div key={agent.stepId || i} className="top-agent-card">
                  <div className="top-agent-rank">{agent.rank || i + 1}</div>
                  <div className="top-agent-content">
                    <div className="top-agent-name">{agent.stepName}</div>
                    <div className="top-agent-reason">{agent.reason}</div>
                  </div>
                  <div className={`top-agent-potential potential-${agent.automationPotential}`}>
                    {POTENTIAL_LABELS[agent.automationPotential] || agent.automationPotential}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Zuordnungen table hidden — data kept in backend for implementation panel */}

        {/* Recommended start point */}
        {mappingData?.summary?.recommendedStartPoint && (
          <div className="mapping-recommendation">
            <span className="rec-label">Empfohlener Start:</span>
            <span className="rec-text">{mappingData.summary.recommendedStartPoint}</span>
          </div>
        )}

        {/* SharePoint bridge + data-at-source note (Topic 15) */}
        {bridgeSteps.length > 0 && (
          <div className="bridge-section">
            <h3 className="bridge-title">SharePoint-Brücke &amp; Datenanbindung</h3>
            <div className="bridge-list">
              {bridgeSteps.map((b, i) => (
                <div key={b.stepId || i} className="bridge-card">
                  <div className="bridge-step-name">{b.stepName}</div>
                  <div className="bridge-text">{b.bridge}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Button */}
        <button className="btn btn-mapping-continue" onClick={onViewInCanvas}>
          Ergebnisse im Prozess ansehen
        </button>
      </div>
    </div>
  );
}
